import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
// Components
import { useExchangeRates } from '@/modules/common';
import { InputFieldWithCurrency } from '@/modules/common/components/molecules/inputs/input-field-with-currency';
import { SingleSelect } from '@/modules/common/components/molecules/selectors/single-select/single-select';
// Types
import {
  BaseFeatureTab,
  BaseTabProps,
  FeatureMode,
  FeatureTabRef,
  SubmitDateRange,
  useTabFormState,
} from '@/modules/common/components/templates/feature-details';
import { Currency } from '@/modules/common/types/currency';
import { RateSource } from '@/modules/common/types/rate-source';
// Project Components
import {
  BillingModelSelector,
  createTeamMemberColumns,
  ProjectDataSelector,
  ProjectRatesOverviewTable,
  TeamMemberColumnContext,
} from '@/modules/projects';
import { ResolveConflictsModal } from '@/modules/projects/components/modals/resolve-conflicts-modal/resolve-conflicts-modal';
// Constants and Config
import { Project, ProjectBillingModel, ProjectStatus } from '@/modules/projects/types/project';
import { ProjectChangeType } from '@/modules/projects/types/project-changes';
import { ChangeDateRange } from '@/modules/projects/types/project-conflicts';
import { ProjectTeamMember } from '@/modules/projects/types/project-team-member';
import {
  getEffectiveRateSource,
  getMemberRate,
  RateCalculationContext,
} from '@/modules/projects/utils';
import {
  projectBillingConflictFields,
  ProjectBillingFieldKey,
  projectBillingGroupedFields,
} from '@/modules/projects/utils/change-conflicts/project-billing-conflict-config';
import {
  filterBillingResolvedValues,
  useProjectBillingSyncRules,
} from '@/modules/projects/utils/change-conflicts/project-billing-sync-rules';
import {
  getDefaultPaymentDetails,
  usePaymentDetailsShort,
} from '@/modules/settings/hooks/payment-details/use-payment-details-short';
import { AuthenticatedRoutes, SettingsRoutes } from '@/router/routes';
import { compareFormValues } from '@/utils/form-state.utils';
import { logger } from '@/utils/logger/logger';

import { useBillingLogic } from '../../../hooks/billing/use-billing-logic';
import { useCheckProjectBillingConflicts } from '../../../hooks/billing/use-check-project-billing-conflicts';
// Hooks and Utilities
import { useRateOverviewTableManagement } from '../../../hooks/billing/use-rate-overview-table-management';
import { useProjectChangeConflictResolution } from '../../../hooks/use-project-change-conflict-resolution';
import { ProjectBillingFormData, ProjectFormData, ProjectTab } from '../../../types';

const TAG = 'ProjectBillingTab:';
const PAYMENT_DETAILS_NONE_VALUE = 'none';

const projectBillingSchema = z.object({
  billingModel: z.nativeEnum(ProjectBillingModel),
  currency: z.string().min(1, 'common:error.required'),
  rateSource: z.nativeEnum(RateSource),
  flatRate: z.number().min(0, 'common:error.positiveNumber').default(0).optional(),
  projectBudget: z.number().min(0, 'common:error.positiveNumber').default(0).optional(),
  paymentDetailsId: z.number().int().positive().nullable().optional(),
});

export interface ProjectBillingTabProps extends BaseTabProps<Project, ProjectBillingFormData> {
  currencies?: Currency[];
  isLoadingCurrencies?: boolean;
  currencyError?: Error | null;
  defaultOrgCurrency?: string;
  defaultRateSource?: RateSource;
  /**
   * Callback to change tabs with optional additional query params
   * Goes through template's unsaved changes confirmation
   */
  onTabChange?: (tab: string, additionalParams?: Record<string, string>) => void;
  /**
   * Callback to directly save data to API after conflict resolution
   * This bypasses the normal form submission flow and conflict detection
   * @param data - The form data to save
   * @param effectiveStartDate - The effective start date for the change (from conflict resolution)
   * @param effectiveEndDate - Optional effective end date for the change
   */
  onDirectSave?: (
    data: ProjectFormData,
    effectiveStartDate?: Date,
    effectiveEndDate?: Date | null
  ) => Promise<void>;
  /**
   * Original change date range (for EditChange mode)
   * Used to determine if the date range was extended for conflict detection
   */
  originalChangeRange?: ChangeDateRange;
}

export interface ProjectBillingTabRef extends FeatureTabRef<ProjectFormData> {}

/**
 * Project billing form component supporting create, edit, and read modes.
 * Handles billing configuration with model transition validation and rate source logic.
 *
 * Features:
 * - Multi-mode operation with role-based field visibility
 * - Draft data persistence for create mode
 * - Billing model transition validation with user warnings
 * - Dynamic form fields based on billing model and rate source
 * - Currency selection with API integration
 * - Real-time validation and dirty state detection
 * - Conditional field rendering based on business logic
 */
export const ProjectBillingTab = forwardRef<ProjectBillingTabRef, ProjectBillingTabProps>(
  (props, ref) => {
    const {
      mode,
      featureData: projectData,
      draftData,
      currencies,
      isLoadingCurrencies = false,
      currencyError,
      defaultOrgCurrency,
      defaultRateSource,
      onTabChange,
      onDirectSave,
      onFormStateChange,
      originalChangeRange,
    } = props;
    const { t } = useTranslation(['authenticated.projects', 'common']);
    const navigate = useNavigate();
    const { checkConflicts, isPending: isCheckingConflicts } = useCheckProjectBillingConflicts();

    const { data: paymentDetailsEntries = [], isLoading: isLoadingPaymentDetails } =
      usePaymentDetailsShort();

    const availableCurrencies = currencyError ? [] : currencies || [];

    /**
     * Builds initial form values based on mode and available data sources.
     * Priority: draftData (create) > projectData (edit/read) > defaults (create)
     */
    const initialValues = useMemo((): ProjectBillingFormData => {
      const fallbackRateSource = defaultRateSource || RateSource.UserExternalRate;

      if (mode === FeatureMode.Create && draftData) {
        return {
          billingModel: draftData.billingModel || ProjectBillingModel.TM,
          currency: draftData.currency || (defaultOrgCurrency ?? ''),
          rateSource: draftData.rateSource || fallbackRateSource,
          flatRate: draftData.flatRate,
          projectBudget: draftData.projectBudget,
          paymentDetailsId:
            draftData.paymentDetailsId ??
            getDefaultPaymentDetails(paymentDetailsEntries)?.id ??
            null,
        };
      }

      if (mode === FeatureMode.Create) {
        return {
          billingModel: ProjectBillingModel.TM,
          currency: defaultOrgCurrency ?? '',
          rateSource: fallbackRateSource,
          flatRate: undefined,
          projectBudget: undefined,
          paymentDetailsId: getDefaultPaymentDetails(paymentDetailsEntries)?.id ?? null,
        };
      }

      // For edit/read mode - use project data with organization defaults as fallback
      return {
        billingModel: projectData?.billing?.billingModel || ProjectBillingModel.TM,
        currency: projectData?.billing?.defaultCurrency?.code ?? defaultOrgCurrency ?? '',
        rateSource: projectData?.billing?.rateSource || fallbackRateSource,
        flatRate: projectData?.billing?.flatRate != null ? projectData.billing.flatRate : undefined,
        projectBudget:
          projectData?.billing?.budget != null
            ? parseFloat(projectData.billing.budget.toFixed(4))
            : undefined,
        paymentDetailsId: projectData?.billing?.paymentDetailsId ?? null,
      };
    }, [mode, projectData, draftData, defaultOrgCurrency, defaultRateSource]);

    const form = useForm<ProjectBillingFormData>({
      resolver: zodResolver(projectBillingSchema),
      mode: 'onChange',
      defaultValues: initialValues,
    });

    // Reset form when initialValues change (e.g., when projectData loads in edit mode)
    useEffect(() => {
      if (mode === FeatureMode.Edit || mode === FeatureMode.Read) {
        form.reset(initialValues);
      }
    }, [initialValues, mode, form]);

    // In Create mode, apply the default payment details once the async query resolves.
    // useForm only reads defaultValues once at mount, so a late-loading default won't be
    // picked up automatically — only patch if the user hasn't touched the field yet.
    useEffect(() => {
      if (mode !== FeatureMode.Create || isLoadingPaymentDetails) return;
      if (form.formState.dirtyFields.paymentDetailsId) return;
      const defaultId = getDefaultPaymentDetails(paymentDetailsEntries)?.id ?? null;
      form.setValue('paymentDetailsId', defaultId, { shouldDirty: false });
    }, [mode, paymentDetailsEntries, isLoadingPaymentDetails, form]);

    // Define archived check function (DRY)
    const isArchived = (project?: Project) => project?.info?.status === ProjectStatus.Archived;

    // Use the tab form state hook
    const {
      isReadMode,
      handleSubmit: baseHandleSubmit,
      getFormData: baseGetFormData,
    } = useTabFormState<Project, ProjectBillingFormData>(props, {
      form,
      initialValues,
      isArchivedFn: isArchived,
      triggerValidationOnMount: true,
      customDirtyStateFn: currentValues => {
        if (!currentValues) return false;

        // Check both form values and team member changes
        const formValuesChanged = compareFormValues(currentValues, initialValues);
        const initialTeamMembers =
          mode === FeatureMode.Create ? [] : projectData?.billing?.userRates || [];
        const teamMembersChanged = compareFormValues(teamMembers, initialTeamMembers);

        return formValuesChanged || teamMembersChanged;
      },
    });

    /**
     * Form data getter with error handling and team members
     */
    const getFormData = (): ProjectFormData => {
      const baseData = baseGetFormData();
      return {
        ...baseData,
        selectedTeamMembers: teamMembers,
        cachedTeamMembers: cachedTeamMembers,
        defaultRateSource,
      } as ProjectFormData;
    };

    // Use conflict resolution hook
    const {
      showConflictModal,
      conflictSnapshots,
      selectedDateRange,
      closeConflictModal,
      handleSubmitWithConflicts,
      handleResolveConflicts,
    } = useProjectChangeConflictResolution<ProjectBillingFormData, ProjectBillingFieldKey>({
      tag: TAG,
      form,
      projectId: projectData?.id ? String(projectData.id) : undefined,
      checkConflicts,
      isCheckingConflicts,
      onFormStateChange,
      getFormData,
      onDirectSave,
      isEditChangeMode: mode === FeatureMode.EditChange,
      originalChangeRange,
    });

    // Get current form values for calculations
    const currentRateSource = useWatch({ control: form.control, name: 'rateSource' });
    const currentFlatRate = useWatch({ control: form.control, name: 'flatRate' });
    const currentCurrency = useWatch({ control: form.control, name: 'currency' });
    const currentValues = useWatch({ control: form.control });
    const currentProjectBudget = useWatch({ control: form.control, name: 'projectBudget' });

    // Track previous currency and budget/team members snapshot for conversion
    const previousCurrencyRef = useRef<string | null>(null);
    const [conversionSnapshot, setConversionSnapshot] = useState<{
      fromCurrency: string;
      budget: number | null;
      members: ProjectTeamMember[] | null;
    } | null>(null);

    // Initialize previousCurrency on mount
    useEffect(() => {
      if (initialValues.currency && !previousCurrencyRef.current) {
        previousCurrencyRef.current = initialValues.currency;
      }
    }, [initialValues.currency]);

    // Fetch exchange rates only when we have a conversion snapshot with values to convert
    const shouldFetchForConversion =
      conversionSnapshot !== null &&
      currentCurrency !== '' &&
      conversionSnapshot.fromCurrency !== currentCurrency;

    const { data: conversionRates, isLoading: isLoadingConversionRates } = useExchangeRates({
      fromCodes: shouldFetchForConversion ? [conversionSnapshot.fromCurrency] : [],
      toCode: currentCurrency || '',
      enabled: shouldFetchForConversion,
    });

    // Initialize team member management
    const {
      teamMembers,
      cachedTeamMembers,
      setTeamMembers,
      loadMembers,
      handleAddSelectedTeamMember,
      handleRateSourceChange,
      handleCustomRateChange,
      handleRemoveMember,
      isRefetchingRates,
      rateRefetchError,
    } = useRateOverviewTableManagement({
      projectData: projectData?.billing?.userRates,
      draftData,
      currentRateSource,
      defaultRateSource,
      flatRateValue: currentFlatRate,
      currency: currentCurrency,
    });

    // Detect currency change and store budget + team members snapshot for conversion
    useEffect(() => {
      if (
        currentCurrency &&
        previousCurrencyRef.current &&
        currentCurrency !== previousCurrencyRef.current
      ) {
        const hasBudget =
          currentProjectBudget !== undefined &&
          currentProjectBudget !== null &&
          currentProjectBudget > 0;
        const hasMembers = teamMembers.length > 0;

        logger.debug(TAG, 'Currency change detected', {
          from: previousCurrencyRef.current,
          to: currentCurrency,
          budgetToConvert: currentProjectBudget,
          teamMembersCount: teamMembers.length,
          hasBudget,
          hasMembers,
        });

        // Only create conversion snapshot if there's something to convert
        if (hasBudget || hasMembers) {
          setConversionSnapshot({
            fromCurrency: previousCurrencyRef.current,
            budget: hasBudget ? currentProjectBudget : null,
            members: hasMembers ? [...teamMembers] : null,
          });
        } else {
          // Nothing to convert — just update the previous currency tracker
          previousCurrencyRef.current = currentCurrency;
        }
      }
      // Only react to currency changes, not budget/team member changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCurrency]);

    // Apply conversion when rates are available
    useEffect(() => {
      if (!conversionSnapshot || isLoadingConversionRates) return;

      const {
        fromCurrency,
        budget: budgetToConvert,
        members: membersToConvert,
      } = conversionSnapshot;

      logger.debug(TAG, 'Conversion effect triggered', {
        fromCurrency,
        budgetToConvert,
        membersToConvert: membersToConvert?.length,
        currentCurrency,
        hasRates: !!conversionRates?.rates[fromCurrency],
        isLoadingConversionRates,
      });

      if (
        currentCurrency &&
        fromCurrency !== currentCurrency &&
        conversionRates?.rates[fromCurrency]
      ) {
        const rate = conversionRates.rates[fromCurrency];

        logger.info(TAG, 'Converting budget and team member rates', {
          from: fromCurrency,
          to: currentCurrency,
          rate,
          originalBudget: budgetToConvert,
          membersToConvert: membersToConvert?.length,
        });

        // Convert budget if needed
        if (budgetToConvert !== null) {
          const convertedBudget = parseFloat((budgetToConvert / rate).toFixed(4));
          form.setValue('projectBudget', convertedBudget, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }

        // Convert team member rates if needed. Merge into the latest teamMembers state
        // (rather than replacing from the pre-refetch snapshot) so this doesn't clobber the
        // per-source rate values that useRateOverviewTableManagement already refetched from
        // the backend for the new currency.
        if (membersToConvert && membersToConvert.length > 0) {
          const convertedValueById = new Map(
            membersToConvert.map(member => [
              member.id,
              {
                projectRateCardValue: member.projectRateCardValue
                  ? member.projectRateCardValue / rate
                  : member.projectRateCardValue,
                customRateValue: member.customRateValue
                  ? member.customRateValue / rate
                  : member.customRateValue,
              },
            ])
          );

          setTeamMembers(prevMembers =>
            prevMembers.map(member => {
              const converted = convertedValueById.get(member.id);
              return converted ? { ...member, ...converted } : member;
            })
          );

          logger.debug(TAG, 'Team member rates converted successfully', {
            originalCount: membersToConvert.length,
          });
        }

        // Clear snapshot and update previous currency after successful conversion
        previousCurrencyRef.current = currentCurrency;
        setConversionSnapshot(null);
      }
    }, [
      conversionRates,
      isLoadingConversionRates,
      currentCurrency,
      conversionSnapshot,
      form,
      setTeamMembers,
    ]);

    // Update form currency when defaultOrgCurrency loads and current currency is empty
    useEffect(() => {
      const currentCurrency = form.getValues('currency');

      // Only set default currency if:
      // 1. defaultOrgCurrency is available
      // 2. current currency is empty
      // 3. projectData is available and doesn't have a currency (not loading state)
      if (
        defaultOrgCurrency &&
        !currentCurrency &&
        projectData &&
        !projectData.billing?.defaultCurrency?.code
      ) {
        form.setValue('currency', defaultOrgCurrency);
      }
    }, [defaultOrgCurrency, form, projectData]);

    const currentBillingModel = useWatch({ control: form.control, name: 'billingModel' });
    const originalBillingModel = projectData?.billing?.billingModel ?? undefined;

    // Initialize billing logic
    const {
      billingModelOptions,
      rateSourceOptions,
      teamMemberRateSourceOptions,
      shouldShowBillingModelWarning,
      showRateSource,
      showFlatRate,
      showProjectBudget,
      shouldShowRateSourceAlert,
    } = useBillingLogic({
      mode,
      originalBillingModel,
      currentBillingModel,
      currentRateSource,
      t,
    });

    // Form field disabled states
    const isRateSourceDisabled =
      isReadMode || currentBillingModel === ProjectBillingModel.NonBillable;
    const isFlatRateDisabled =
      isReadMode || currentBillingModel === ProjectBillingModel.NonBillable;
    const isCurrencyDisabled = isReadMode;
    // Overheads have no stored currency: they follow the project's billing change effective on their date.
    const shouldShowCurrencyChangeNotice =
      (mode === FeatureMode.Edit || mode === FeatureMode.EditChange) &&
      currentCurrency !== '' &&
      currentCurrency !== initialValues.currency;

    // Create a ref to store the current values so we don't need it in dependencies
    const currentValuesRef = useRef(currentValues);
    currentValuesRef.current = currentValues;

    // Create a ref to store the rateContext so callbacks don't go stale
    const rateContextRef = useRef<RateCalculationContext>({
      currentRateSource,
      currentValues,
      projectData: { teamMembers },
      defaultRateSource,
    });
    rateContextRef.current = {
      currentRateSource,
      currentValues,
      projectData: { teamMembers },
      defaultRateSource,
    };

    /**
     * Create table context for column definitions
     * Using refs for changing values to break the dependency cycle and prevent avatar blinking.
     * Only stable values (callbacks, read mode, rate source options) are in the dependency array —
     * frequently-changing values (currentValues, teamMembers, rateContext) are accessed via refs.
     */
    const tableContext: TeamMemberColumnContext = useMemo(
      () => ({
        t,
        currentRateSource,
        get currentValues() {
          const values = currentValuesRef.current;
          return {
            ...values,
            billingModel: values.billingModel || ProjectBillingModel.TM,
            currency: values.currency || '',
            rateSource: values.rateSource || RateSource.UserExternalRate,
            selectedTeamMembers: values.selectedTeamMembers?.filter(
              member => member.id && member.name && member.type && member.activeRateSource
            ) as ProjectTeamMember[] | undefined,
            cachedTeamMembers: values.cachedTeamMembers?.filter(
              member => member.id && member.name && member.type && member.activeRateSource
            ) as ProjectTeamMember[] | undefined,
          };
        },
        isReadMode,
        rateSourceOptions,
        teamMemberRateSourceOptions,
        getEffectiveRateSource: (member: ProjectTeamMember) =>
          getEffectiveRateSource(member, rateContextRef.current),
        calculateMemberRate: (member: ProjectTeamMember) =>
          getMemberRate(member, rateContextRef.current),
        handleRateSourceChange,
        handleCustomRateChange,
        handleRemoveMember,
        isRefetchingRates,
        rateRefetchError,
      }),
      [
        t,
        currentRateSource,
        isReadMode,
        rateSourceOptions,
        teamMemberRateSourceOptions,
        handleRateSourceChange,
        handleCustomRateChange,
        handleRemoveMember,
        isRefetchingRates,
        rateRefetchError,
      ]
    );

    /**
     * Create table columns using context
     */
    const columns: ColumnDef<ProjectTeamMember>[] = useMemo(() => {
      return createTeamMemberColumns(tableContext);
    }, [tableContext]);

    /**
     * Initialize React Table
     */
    const table = useReactTable({
      data: teamMembers,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getRowId: row => row.id, // Ensure stable row identification
    });

    // Track if this is the initial mount to prevent useEffect from running on first render
    const isInitialMount = useRef(true);

    /**
     * Configuration for rate source alerts with navigation
     */
    const rateSourceAlertConfig = useMemo(() => {
      const configs: Record<
        RateSource,
        { translationKey: string; linkKey: string; route: string } | null
      > = {
        [RateSource.GlobalDefaultRate]: {
          translationKey: 'details.tabs.billing.rateSourceAlert',
          linkKey: 'orgSettings',
          route: SettingsRoutes.Finance,
        },
        [RateSource.UserExternalRate]: {
          translationKey: 'details.tabs.billing.rateSourceAlertUserExternal',
          linkKey: 'teamMemberDetails',
          route: AuthenticatedRoutes.Team,
        },
        [RateSource.FlatRate]: null,
        [RateSource.SeniorityBasedRate]: null,
        [RateSource.RoleBasedRate]: null,
        [RateSource.ProjectRateCard]: null,
        [RateSource.Custom]: null,
        [RateSource.ProjectBased]: null,
      };

      return configs[currentRateSource];
    }, [currentRateSource]);

    /**
     * Creates a clickable link component with navigation and keyboard support
     */
    const createNavigableLink = (route: string) => (
      <span
        role="button"
        tabIndex={0}
        onClick={() => navigate(route)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(route);
          }
        }}
        className="cursor-pointer font-medium text-foreground underline hover:no-underline"
      ></span>
    );

    // Clear conditional fields only when the billing model changes.
    useEffect(() => {
      // Skip on initial mount - only run when user actually changes billing model
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }

      try {
        if (currentBillingModel === ProjectBillingModel.NonBillable) {
          form.setValue('projectBudget', undefined);
          form.setValue('rateSource', RateSource.FlatRate);
          form.setValue('flatRate', 0);
        } else if (originalBillingModel === ProjectBillingModel.NonBillable) {
          // When changing from NonBillable to any other model, set rate source to default
          const fallbackRateSource = defaultRateSource || RateSource.UserExternalRate;
          form.setValue('rateSource', fallbackRateSource);
          form.setValue('flatRate', undefined);
        }
      } catch (error) {
        logger.error(TAG, 'Error updating form values on billing model change:', error);
      }
    }, [currentBillingModel, form, defaultRateSource, originalBillingModel]);

    const getConflictModalOriginalFormValues = useCallback(
      () => ({ ...form.getValues() }) as Record<string, unknown>,
      [form]
    );

    const conflictModalSyncRules = useProjectBillingSyncRules(
      getConflictModalOriginalFormValues,
      teamMembers
    );

    // Snapshot form values keyed to the current set of conflict snapshots.
    // conflictSnapshots only changes when new conflicts are detected (i.e. when
    // the modal is about to open), so this memo is stable while the modal is open
    // and prevents useConflictResolution from resetting its internal form on
    // every parent re-render.
    const conflictFormValues = useMemo(
      () =>
        ({
          ...form.getValues(),
          userRates: teamMembers.map(member => {
            if (member.activeRateSource === RateSource.FlatRate) {
              return { ...member, customRateValue: form.getValues().flatRate || 0 };
            }
            return member;
          }),
        }) as Record<ProjectBillingFieldKey, any>,
      [conflictSnapshots] // eslint-disable-line react-hooks/exhaustive-deps
    );

    /**
     * Submit handler with conflict detection
     */
    const handleSubmit = async (dateRange?: SubmitDateRange): Promise<void> => {
      // Skip conflict detection for Create mode
      if (mode === FeatureMode.Create) {
        await baseHandleSubmit();
        return;
      }

      // Use the conflict resolution hook for Edit/EditChange modes
      await handleSubmitWithConflicts(baseHandleSubmit, dateRange);
    };

    const tabContent = (
      <Form {...form}>
        <form className="space-y-4">
          {/* Billing Configuration section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('details.tabs.billing.configurationTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <BillingModelSelector
                form={form}
                isReadMode={isReadMode}
                billingModelOptions={billingModelOptions}
                showWarning={shouldShowBillingModelWarning}
                currentBillingModel={currentBillingModel}
                t={t}
              />

              <ProjectDataSelector
                name={'currency'}
                title={t('details.tabs.billing.currency')}
                placeholder={t('details.tabs.billing.selectCurrency')}
                emptyMessage={t('details.tabs.billing.noCurrenciesFound')}
                form={form}
                options={availableCurrencies.map(currency => ({
                  value: currency.code,
                  label: currency.toString(),
                }))}
                isLoading={isLoadingCurrencies}
                isReadMode={isCurrencyDisabled}
                t={t}
              />

              {shouldShowCurrencyChangeNotice && (
                <Alert variant="info">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs leading-4">
                    {t('details.tabs.billing.currencyChangeOverheadsNotice')}
                  </AlertDescription>
                </Alert>
              )}

              {showProjectBudget && (
                <InputFieldWithCurrency
                  name="projectBudget"
                  label={t('details.tabs.billing.projectBudget')}
                  placeholder={t('details.tabs.billing.projectBudgetPlaceholder')}
                  control={form.control}
                  currency={currentValues.currency}
                  isReadMode={isReadMode}
                  isLoading={isLoadingConversionRates && conversionSnapshot?.budget !== null}
                  maxDecimalPlaces={2}
                />
              )}

              {showRateSource && (
                <ProjectDataSelector
                  name={'rateSource'}
                  title={t('details.tabs.billing.rateSource.label')}
                  placeholder={t('details.tabs.billing.rateSource.placeholder')}
                  form={form}
                  options={rateSourceOptions.map(option => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  isReadMode={isRateSourceDisabled}
                  searchable={false}
                  isLoading={false}
                  t={t}
                />
              )}

              {shouldShowRateSourceAlert && rateSourceAlertConfig && (
                <Alert className="border-border bg-muted/50">
                  <Info className="h-4 w-4 text-foreground" />
                  <AlertDescription className="text-xs leading-4 text-foreground">
                    <Trans
                      i18nKey={rateSourceAlertConfig.translationKey}
                      t={t}
                      components={{
                        [rateSourceAlertConfig.linkKey]: createNavigableLink(
                          rateSourceAlertConfig.route
                        ),
                      }}
                    />
                  </AlertDescription>
                </Alert>
              )}

              {showFlatRate && (
                <InputFieldWithCurrency
                  name="flatRate"
                  label={t('details.tabs.billing.hourlyRate')}
                  placeholder={t('details.tabs.billing.flatRatePlaceholder')}
                  control={form.control}
                  currency={currentValues.currency}
                  isReadMode={isFlatRateDisabled}
                  maxDecimalPlaces={2}
                />
              )}

              {(() => {
                const noneOption = {
                  value: PAYMENT_DETAILS_NONE_VALUE,
                  label: t('details.tabs.billing.paymentDetails.none'),
                };
                const options = [
                  noneOption,
                  ...paymentDetailsEntries.map(entry => ({
                    value: entry.id.toString(),
                    label: entry.name,
                  })),
                ];
                const currentPaymentDetailsId = form.watch('paymentDetailsId');
                const selectedOption =
                  options.find(
                    o => o.value === (currentPaymentDetailsId?.toString() ?? noneOption.value)
                  ) ?? noneOption;

                return (
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium leading-none">
                      {t('details.tabs.billing.paymentDetails.label')}
                    </span>
                    <SingleSelect
                      control={form.control}
                      name="paymentDetailsId"
                      label={
                        <span className="sr-only">
                          {t('details.tabs.billing.paymentDetails.label')}
                        </span>
                      }
                      placeholder={t('details.tabs.billing.paymentDetails.placeholder')}
                      options={options}
                      selectedOption={selectedOption}
                      disabled={isReadMode || isLoadingPaymentDetails}
                      loading={isLoadingPaymentDetails}
                      onChange={value =>
                        form.setValue(
                          'paymentDetailsId',
                          value === noneOption.value ? null : Number(value),
                          { shouldDirty: true, shouldValidate: true }
                        )
                      }
                    />
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* Current Rate Overview section */}
          {showRateSource && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>{t('details.tabs.billing.currentRateOverview')}</CardTitle>
                {mode !== FeatureMode.Create && onTabChange && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => {
                      onTabChange(ProjectTab.Changes, {
                        changeType: ProjectChangeType.Billing,
                      });
                    }}
                  >
                    {t('details.tabs.billing.showBillingChanges')}
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <ProjectRatesOverviewTable
                  table={table}
                  isReadMode={isReadMode}
                  loadMembers={loadMembers}
                  onAddSelectedTeamMember={handleAddSelectedTeamMember}
                  hasTeamMembers={teamMembers.length > 0}
                  teamMembersCount={teamMembers.length}
                />
              </CardContent>
            </Card>
          )}
        </form>
      </Form>
    );

    return (
      <>
        <BaseFeatureTab
          {...props}
          ref={ref}
          isArchivedFn={isArchived}
          onSubmit={handleSubmit}
          isValid={form.formState.isValid}
          getFormData={getFormData}
          isCheckingConflicts={isCheckingConflicts}
        >
          {tabContent}
        </BaseFeatureTab>

        {/* Conflict Resolution Modal */}
        {selectedDateRange && (
          <ResolveConflictsModal<ProjectBillingFieldKey>
            isOpen={showConflictModal}
            onClose={closeConflictModal}
            snapshots={conflictSnapshots}
            currentFormValues={conflictFormValues}
            selectedDateRange={selectedDateRange}
            fields={projectBillingConflictFields}
            groupedFields={projectBillingGroupedFields}
            onResolve={handleResolveConflicts}
            t={t}
            translationNamespace="authenticated.projects"
            context={{
              currency: availableCurrencies.find(c => c.code === form.getValues().currency),
              rateSource: form.getValues().rateSource,
            }}
            fieldSyncRules={conflictModalSyncRules}
            onBeforeResolve={filterBillingResolvedValues}
          />
        )}
      </>
    );
  }
);
